// ABOUTME: Credentials input step for the DataSourceWizard.
// ABOUTME: Collects Databricks workspace URL, HTTP path, and access token.

import { useState, useEffect } from 'react';
import { Button, Input } from '@/components/ui';
import type { ConnectionCredentials } from '@/services/api/dataSourceClient';

interface CredentialsStepProps {
  initialCredentials: ConnectionCredentials;
  onNext: (credentials: ConnectionCredentials) => void;
  onBack: () => void;
  onCancel: () => void;
}

export function CredentialsStep({
  initialCredentials,
  onNext,
  onBack,
  onCancel,
}: CredentialsStepProps) {
  const [workspaceUrl, setWorkspaceUrl] = useState(initialCredentials.workspaceUrl);
  const [httpPath, setHttpPath] = useState(initialCredentials.httpPath);
  const [accessToken, setAccessToken] = useState(initialCredentials.accessToken);

  const [errors, setErrors] = useState<{
    workspaceUrl?: string;
    httpPath?: string;
    accessToken?: string;
  }>({});

  // Validate inputs
  const isValid = workspaceUrl.trim() !== '' && httpPath.trim() !== '' && accessToken.trim() !== '';

  const validateAndSubmit = () => {
    const newErrors: typeof errors = {};

    // Validate workspace URL
    if (!workspaceUrl.trim()) {
      newErrors.workspaceUrl = 'Workspace URL is required';
    } else if (!workspaceUrl.includes('.databricks.com') && !workspaceUrl.includes('.azuredatabricks.net')) {
      newErrors.workspaceUrl = 'Please enter a valid Databricks workspace URL';
    }

    // Validate HTTP path
    if (!httpPath.trim()) {
      newErrors.httpPath = 'HTTP path is required';
    } else if (!httpPath.startsWith('/sql/')) {
      newErrors.httpPath = 'HTTP path should start with /sql/';
    }

    // Validate access token
    if (!accessToken.trim()) {
      newErrors.accessToken = 'Access token is required';
    } else if (!accessToken.startsWith('dapi') && accessToken.length < 20) {
      newErrors.accessToken = 'Please enter a valid Databricks personal access token';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext({
        workspaceUrl: workspaceUrl.trim(),
        httpPath: httpPath.trim(),
        accessToken: accessToken.trim(),
      });
    }
  };

  // Clear error when field is edited
  useEffect(() => {
    if (errors.workspaceUrl && workspaceUrl) {
      setErrors((prev) => ({ ...prev, workspaceUrl: undefined }));
    }
  }, [workspaceUrl, errors.workspaceUrl]);

  useEffect(() => {
    if (errors.httpPath && httpPath) {
      setErrors((prev) => ({ ...prev, httpPath: undefined }));
    }
  }, [httpPath, errors.httpPath]);

  useEffect(() => {
    if (errors.accessToken && accessToken) {
      setErrors((prev) => ({ ...prev, accessToken: undefined }));
    }
  }, [accessToken, errors.accessToken]);

  return (
    <div className="px-6 py-8">
      <h2 className="text-xl font-semibold text-surface-900 mb-2">
        Enter Your Credentials
      </h2>
      <p className="text-surface-600 mb-6">
        Provide your Databricks connection details to continue.
      </p>

      <div className="space-y-5 mb-8">
        <div>
          <Input
            label="Workspace URL"
            placeholder="https://dbc-xxxxx.cloud.databricks.com"
            value={workspaceUrl}
            onChange={(e) => setWorkspaceUrl(e.target.value)}
            error={errors.workspaceUrl}
            helperText="e.g., https://dbc-xxxxx.cloud.databricks.com"
          />
        </div>

        <div>
          <Input
            label="HTTP Path"
            placeholder="/sql/1.0/warehouses/xxxx"
            value={httpPath}
            onChange={(e) => setHttpPath(e.target.value)}
            error={errors.httpPath}
            helperText="Found in your SQL Warehouse connection details (e.g., /sql/1.0/warehouses/xxxx)"
          />
        </div>

        <div>
          <Input
            label="Personal Access Token"
            type="password"
            placeholder="dapi..."
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            error={errors.accessToken}
            helperText="Create one in User Settings > Developer > Access tokens"
          />
        </div>
      </div>

      <div className="bg-surface-50 rounded-lg p-4 mb-8">
        <h3 className="text-sm font-medium text-surface-700 mb-2">
          Where to find these values
        </h3>
        <ol className="text-sm text-surface-600 space-y-2 list-decimal list-inside">
          <li>
            Open your Databricks workspace
          </li>
          <li>
            Go to <strong>SQL Warehouses</strong> and select your warehouse
          </li>
          <li>
            Click <strong>Connection Details</strong> tab
          </li>
          <li>
            Copy the <strong>Server Hostname</strong> and <strong>HTTP Path</strong>
          </li>
          <li>
            For the token, go to <strong>User Settings → Developer → Access Tokens</strong>
          </li>
        </ol>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={validateAndSubmit} disabled={!isValid}>
            Test Connection
          </Button>
        </div>
      </div>
    </div>
  );
}
